import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PriceHistoryChart } from './PriceHistoryChart';
import { TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';

interface TickerTrade {
  politician: string;
  party: string;
  type: string;
  traded: string;
  size: string;
  price: string;
}

interface TickerScorecardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticker: string;
  issuer: string;
  trades: TickerTrade[];
}

export function TickerScorecard({ open, onOpenChange, ticker, issuer, trades }: TickerScorecardProps) {
  const [range] = useState<'6mo'>('6mo');

  const buys = trades.filter(t => ['buy', 'purchase'].includes(t.type.toLowerCase())).length;
  const sells = trades.filter(t => ['sell', 'sale'].includes(t.type.toLowerCase())).length;
  const net = buys - sells;
  const uniquePoliticians = [...new Set(trades.map(t => t.politician))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <code className="text-lg bg-muted px-2 py-0.5 rounded font-mono">{ticker}</code>
            <span className="text-base font-normal text-muted-foreground">{issuer}</span>
            <a
              href={`https://finance.yahoo.com/quote/${ticker}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </a>
          </DialogTitle>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg border bg-card p-3 text-center">
            <p className="text-xs text-muted-foreground">Total Trades</p>
            <p className="text-xl font-bold text-foreground">{trades.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-3 text-center">
            <p className="text-xs text-muted-foreground">Buys</p>
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{buys}</p>
          </div>
          <div className="rounded-lg border bg-card p-3 text-center">
            <p className="text-xs text-muted-foreground">Sells</p>
            <p className="text-xl font-bold text-rose-600 dark:text-rose-400">{sells}</p>
          </div>
          <div className="rounded-lg border bg-card p-3 text-center">
            <p className="text-xs text-muted-foreground">Net</p>
            <p className={`text-xl font-bold ${net > 0 ? 'text-emerald-600 dark:text-emerald-400' : net < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
              {net > 0 ? '+' : ''}{net}
            </p>
          </div>
        </div>

        {/* Congress Sentiment */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">Congressional Sentiment:</span>
          {net > 2 ? (
            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">
              <TrendingUp className="h-3 w-3 mr-1" /> Strong Buy Signal
            </Badge>
          ) : net > 0 ? (
            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">
              <TrendingUp className="h-3 w-3 mr-1" /> Net Buying
            </Badge>
          ) : net < -2 ? (
            <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30">
              <TrendingDown className="h-3 w-3 mr-1" /> Strong Sell Signal
            </Badge>
          ) : net < 0 ? (
            <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30">
              <TrendingDown className="h-3 w-3 mr-1" /> Net Selling
            </Badge>
          ) : (
            <Badge variant="secondary">Neutral</Badge>
          )}
        </div>

        {/* Price Chart */}
        {ticker && <PriceHistoryChart ticker={ticker} />}

        {/* Politicians Trading This Stock */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">Politicians Trading ({uniquePoliticians.length})</h4>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {trades.slice(0, 20).map((t, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-muted/50">
                <span className="font-medium text-foreground">{t.politician}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-xs ${
                    ['buy', 'purchase'].includes(t.type.toLowerCase())
                      ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                      : ['sell', 'sale'].includes(t.type.toLowerCase())
                        ? 'border-rose-500/30 text-rose-700 dark:text-rose-400'
                        : ''
                  }`}>
                    {t.type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{t.size}</span>
                  <span className="text-xs text-muted-foreground">{t.traded}</span>
                </div>
              </div>
            ))}
            {trades.length > 20 && (
              <p className="text-xs text-muted-foreground text-center py-1">+ {trades.length - 20} more trades</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
